import React, { useState, useEffect } from "react";
import { leadSource } from "../lib/leadSource";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useQuote } from "../context/QuoteContext";
import { navLinks } from "../data/mock";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2 } from "lucide-react";

const serviceOptions = navLinks[2].children.map((c) => c.label);
const projectTypes = [
  "Office / Conference Room",
  "Restaurant / Bar",
  "Retail / Digital Signage",
  "House of Worship / Venue",
  "Hospitality / Multi-Family",
  "Other",
];

const QuoteModal = () => {
  const { open, setOpen, preselected, preselectedBrand } = useQuote();
  const { toast } = useToast();
  const location = useLocation();
  const [brand, setBrand] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    business_name: "",
    project_type: "",
    num_units: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const isCommercial = form.service === "Commercial AV";
  const isManufacturer = !!brand;

  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setBrand(preselectedBrand || "");
      const onCommercialPage = location.pathname.includes("/services/commercial-av");
      const service = preselected || (onCommercialPage ? "Commercial AV" : "");
      setForm((f) => ({
        ...f,
        service,
        message: preselectedBrand
          ? `I'm interested in ${preselectedBrand} products and professional design & installation.`
          : "",
        business_name: "",
        project_type: "",
        num_units: "",
      }));
    }
  }, [open, preselected, preselectedBrand, location.pathname]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        message: brand ? `[Brand: ${brand}] ${form.message}` : form.message,
        ...leadSource(),
      };
      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      toast({
        title: "Quote request sent!",
        description: "We'll email you shortly to confirm your request.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[480px]">
        {submitted ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#3b9dff] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#0a1f44] mb-2">
              Thank you, {form.name.split(" ")[0] || "friend"}!
            </h3>
            <p className="text-slate-500 text-sm">
              Your quote request has been received. Our team will reach out to
              you shortly to confirm the details.
            </p>
            <button
              onClick={() => setOpen(false)}
              className="mt-6 px-6 py-3 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-extrabold text-[#0a1f44]">
                {isManufacturer
                  ? "Request a Quote"
                  : isCommercial
                  ? "Request a Commercial Quote"
                  : "Get a Free Quote"}
              </DialogTitle>
              <DialogDescription>
                {isManufacturer
                  ? `Tell us about your project and we'll design & quote a ${brand} system.`
                  : isCommercial
                  ? "Tell us about your business project and we'll follow up with a tailored proposal."
                  : "Tell us what you need and we'll get back to you fast."}
              </DialogDescription>
            </DialogHeader>
            {isManufacturer && (
              <div className="flex items-center gap-2 -mt-1" data-testid="quote-brand-chip">
                <span className="text-xs text-slate-500">Brand:</span>
                <span className="inline-flex items-center gap-1 bg-[#3b9dff]/10 text-[#2b8ae8] font-bold text-xs px-3 py-1 rounded-full ring-1 ring-[#3b9dff]/20">
                  {brand}
                </span>
              </div>
            )}
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  data-testid="quote-modal-name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="John Doe"
                  className="mt-1.5"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                  data-testid="quote-modal-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                  data-testid="quote-modal-phone"
                    required
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(252) 000-0000"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label>Service needed</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isCommercial && (
                <div className="space-y-4 rounded-xl bg-[#f5f8ff] border border-[#e6eefc] p-4" data-testid="commercial-fields">
                  <p className="text-xs font-bold uppercase tracking-wide text-[#2b8ae8]">Business details</p>
                  <div>
                    <Label htmlFor="business_name">Business name</Label>
                    <Input
                      id="business_name"
                      value={form.business_name}
                      onChange={(e) => update("business_name", e.target.value)}
                      placeholder="Acme Corp / Joe's Sports Bar"
                      className="mt-1.5 bg-white"
                      data-testid="quote-business-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Project type</Label>
                      <Select
                        value={form.project_type}
                        onValueChange={(v) => update("project_type", v)}
                      >
                        <SelectTrigger className="mt-1.5 bg-white" data-testid="quote-project-type">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectTypes.map((p) => (
                            <SelectItem key={p} value={p}>
                              {p}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="num_units">Rooms / displays</Label>
                      <Input
                        id="num_units"
                        value={form.num_units}
                        onChange={(e) => update("num_units", e.target.value)}
                        placeholder="e.g. 6"
                        className="mt-1.5 bg-white"
                        data-testid="quote-num-units"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  data-testid="quote-modal-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your project..."
                  className="mt-1.5"
                  rows={3}
                />
              </div>
              <button
                type="submit"
                data-testid="quote-modal-submit"
                disabled={loading}
                className="w-full py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors disabled:opacity-60"
              >
                {loading ? "Sending..." : "Request Quote"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
